/**
 * Phase 86 Plan 86-06 Task 1 — the My Tasks keyboard model
 * (`.planning/design/mytasks-compost/README.md`, "Keyboard" +
 * "Accessibility").
 *
 * Owns the cursor index (the row currently under keyboard control) and the
 * four bound shortcuts: `j`/`ArrowDown` and `k`/`ArrowUp` move the cursor
 * (clamped, never wrapping past either end), `Enter` opens the task under
 * the cursor, `T` triggers TOUCH on it (a no-op when the row has no touch
 * action), and `⌘K`/`Ctrl+K` focuses the search field without inserting its
 * own character.
 *
 * `MyTasksPage` calls this hook and holds onto the returned `cursorIndex` /
 * `cursorTaskId` — "the page owns the state" in the sense that a hook's
 * `useState` lives in the component instance that calls it; the page then
 * hands `cursorTaskId` down through `MyTasksZonedList` to the row that
 * matches it, which is what marks it visually + in the markup (Task 2).
 *
 * T-86-21 (threat model) — every shortcut is guarded against firing while
 * the user is typing in a text field, inside the comment composer, or with
 * a dialog/menu open (`isTypingTarget`): a keystroke must never fire a
 * mutation while someone is mid-sentence writing a comment. The guard is
 * the SAME check for all four shortcuts, including `⌘K` itself — the plan's
 * own instruction is to "guard every one of them", not just the row-cursor
 * ones.
 *
 * T-86-22 — the listener attaches and detaches with the page's lifetime
 * (the `useEffect` cleanup below); disabling via the `enabled` flag or
 * unmounting the calling component both remove it.
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export interface UseMyTasksKeyboardOptions {
  /** The visible, currently-rendered task ids, in EXACT render order —
   * whichever sort mode / zone grouping is active. Zone bands are never
   * part of this list, so cursor movement automatically skips them. */
  taskIds: number[];
  /** `Enter` — opens the task under the cursor. */
  onOpen: (taskId: number) => void;
  /** `T` — triggers TOUCH on the task under the cursor. */
  onTouch: (taskId: number) => void;
  /** Optional eligibility check for `T`; when it returns false for the
   * cursor's task id, `T` is a no-op (matches "does nothing on a row that
   * has no touch action" — README "Keyboard"). Omitting it means every row
   * is treated as touch-eligible. */
  canTouch?: (taskId: number) => boolean;
  /** The search `<input>` to focus on `⌘K`/`Ctrl+K`. */
  searchInputRef: RefObject<HTMLInputElement | null>;
  /** Master on/off switch — e.g. false while the page is still loading. */
  enabled?: boolean;
}

export interface UseMyTasksKeyboardResult {
  cursorIndex: number;
  /** `taskIds[cursorIndex]`, or null when there is no cursor (index -1 or
   * the list is empty). */
  cursorTaskId: number | null;
  setCursorIndex: (index: number) => void;
}

/** T-86-21 — true when a shortcut must NOT fire: the user is typing in a
 * text field, a `contentEditable` region, or focus is inside an open
 * dialog/menu (the comment composer, REVIVE's menu, the bulk-close
 * confirmation — every one of 86-05's panels uses `role="dialog"` or
 * `role="menu"` and moves focus inside itself on open). */
function isTypingTarget(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  if (el.closest('[role="dialog"], [role="menu"]')) return true;
  return false;
}

function clampIndex(index: number, length: number): number {
  if (length <= 0) return -1;
  return Math.max(0, Math.min(index, length - 1));
}

export function useMyTasksKeyboard({
  taskIds,
  onOpen,
  onTouch,
  canTouch,
  searchInputRef,
  enabled = true,
}: UseMyTasksKeyboardOptions): UseMyTasksKeyboardResult {
  const [cursorIndex, setCursorIndexState] = useState(-1);

  // Refs kept in sync every render (the `useEscapeHandler.ts` pattern
  // already used elsewhere in this codebase) so the single, long-lived
  // `keydown` listener below always reads the latest values without
  // needing to re-attach itself on every prop change.
  const taskIdsRef = useRef(taskIds);
  taskIdsRef.current = taskIds;
  const cursorIndexRef = useRef(cursorIndex);
  cursorIndexRef.current = cursorIndex;
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const onTouchRef = useRef(onTouch);
  onTouchRef.current = onTouch;
  const canTouchRef = useRef(canTouch);
  canTouchRef.current = canTouch;

  // Keep the cursor inside bounds when the underlying list shrinks or
  // reorders under it — e.g. TOUCH just moved this row out of the stale
  // zone into fresh, changing `taskIds.length` and its order.
  useEffect(() => {
    setCursorIndexState((prev) => (prev === -1 ? -1 : clampIndex(prev, taskIds.length)));
  }, [taskIds]);

  const setCursorIndex = useCallback((index: number) => {
    setCursorIndexState(clampIndex(index, taskIdsRef.current.length));
  }, []);

  useEffect(() => {
    if (!enabled) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (isTypingTarget(document.activeElement)) return;

      // ⌘K / Ctrl+K — focus search. `preventDefault` stops the browser's
      // own default action for the key combo; since focus moves to a
      // DIFFERENT element than the one that received this keydown, no
      // character from this keypress is ever inserted into the search
      // field itself.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }

      const ids = taskIdsRef.current;

      if (e.key === 'j' || e.key === 'ArrowDown') {
        if (ids.length === 0) return;
        e.preventDefault();
        setCursorIndexState((prev) => clampIndex(prev + 1, ids.length));
        return;
      }

      if (e.key === 'k' || e.key === 'ArrowUp') {
        if (ids.length === 0) return;
        e.preventDefault();
        setCursorIndexState((prev) => clampIndex(prev - 1, ids.length));
        return;
      }

      if (e.key === 'Enter') {
        const idx = cursorIndexRef.current;
        const id = idx >= 0 ? ids[idx] : undefined;
        if (id == null) return;
        e.preventDefault();
        onOpenRef.current(id);
        return;
      }

      if (e.key === 't' || e.key === 'T') {
        const idx = cursorIndexRef.current;
        const id = idx >= 0 ? ids[idx] : undefined;
        if (id == null) return;
        if (canTouchRef.current && !canTouchRef.current(id)) return;
        e.preventDefault();
        onTouchRef.current(id);
        return;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled, searchInputRef]);

  const cursorTaskId = cursorIndex >= 0 ? taskIds[cursorIndex] ?? null : null;

  return { cursorIndex, cursorTaskId, setCursorIndex };
}
